import { signalColourFor, signalStateAt, type SignalColour } from './signals';
import type { CityLayout, SignalVisuals } from './types';

/**
 * Signal-head lamp state (world-artist side of the signal API).
 *
 * Signal TIMING is owned by the traffic module's `signals` service (src/traffic/signalController.ts):
 * it calls `setColour` on our `signalVisuals` service for every approach on start and whenever an
 * approach changes. Until it does (or in tests without traffic) lamps fall back to the layout timer.
 * `lamps()` adds the UK red+amber "get ready" aspect when the timer is about to turn the approach green.
 */
export interface SignalLamps {
  red: boolean;
  amber: boolean;
  green: boolean;
}

export interface SignalLights extends SignalVisuals {
  /** Colour the lamps currently show for approach `edgeId` into `intersectionId`. */
  colour(intersectionId: number, edgeId: number): SignalColour;
  /** Lamp state for the approach; written into `out` when given (the signal view reads every head every frame). */
  lamps(intersectionId: number, edgeId: number, out?: SignalLamps): SignalLamps;
}

function setLamps(out: SignalLamps | undefined, red: boolean, amber: boolean, green: boolean): SignalLamps {
  if (!out) return { red, amber, green };
  out.red = red;
  out.amber = amber;
  out.green = green;
  return out;
}

export function createSignalLights(layout: CityLayout, clock: () => number): SignalLights {
  const driven = new Map<number, SignalColour>();
  const key = (i: number, e: number): number => i * 4096 + e;
  const colourOf = (i: number, e: number): SignalColour => {
    const d = driven.get(key(i, e));
    if (d) return d;
    const it = layout.intersections[i];
    return it ? signalColourFor(it, e, clock()) : 'green';
  };
  return {
    setColour(i, e, c) {
      driven.set(key(i, e), c);
    },
    colour(i, e) {
      const it = layout.intersections[i];
      if (!it || !it.signal) return 'green';
      return colourOf(i, e);
    },
    lamps(i, e, out) {
      const it = layout.intersections[i];
      if (!it || !it.signal) return setLamps(out, false, false, false);
      const c = colourOf(i, e);
      if (c === 'green') return setLamps(out, false, false, true);
      if (c === 'amber') return setLamps(out, false, true, false);
      const t = clock();
      // Red+amber only when the lamps agree with the timer (i.e. nobody is overriding them).
      if (signalColourFor(it, e, t) === 'red') {
        const sig = it.signal;
        const st = signalStateAt(sig, t);
        const next = sig.phases[(st.phaseIndex + 1) % sig.phases.length]!;
        if (st.remaining < 1.5 && next.green.includes(e)) return setLamps(out, true, true, false);
      }
      return setLamps(out, true, false, false);
    },
  };
}
